import React, { useState, useEffect } from 'react';
import { FaCog } from 'react-icons/fa';
import { HiOutlineArrowPath, HiOutlineTrash, HiOutlineClipboardDocument } from 'react-icons/hi2';
import { X } from 'react-bootstrap-icons';
import { reactivateUserInvitation, deleteUserInvitation } from '../../../../../../../../crud/users_api/user_invitations';
import { Modal } from 'react-bootstrap';
import UserInvitation from './components/UserInvitation/UserInvitation';
import { toast } from 'react-toastify';
import './TableUserInvitations.scss';

interface TableUserInvitationsProps {
  data: any[];
  setData: (data: any[]) => void;
  hasPermission: boolean;
  OriginalInvitations: any[];
  setOriginalInvitations: (data: any[]) => void;
  showInvitationModal: boolean;
  setShowInvitationModal: (show: boolean) => void;
}

const TableUserInvitations: React.FC<TableUserInvitationsProps> = ({
  data,
  setData,
  hasPermission,
  OriginalInvitations,
  setOriginalInvitations,
  showInvitationModal,
  setShowInvitationModal
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<any | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const baseUrl = window.location.origin;

  // Calcula las horas restantes de validez de la invitación
  const calculateRemainingHours = (item: any) => {
    let expirationTime: number | null = null;

    if (item.expiration_timestamp) {
      expirationTime = new Date(item.expiration_timestamp).getTime();
    } else if (item.timestamp) {
      const ts = new Date(item.timestamp).getTime();
      // Si la invitación es válida, calcula el tiempo restante pero nunca muestres más de 27 horas
      if (item.valid) {
        const maxExpiration = ts + 27 * 60 * 60 * 1000;
        expirationTime = Math.min(maxExpiration, ts + 24 * 60 * 60 * 1000 + (Date.now() - ts));
      } else {
        expirationTime = ts + 24 * 60 * 60 * 1000;
      }
    }

    if (!expirationTime) return 0;
    const currentTime = Date.now();
    const remainingTime = expirationTime - currentTime;
    // Nunca mostrar más de 27 horas
    const hours = remainingTime > 0 ? Math.floor(remainingTime / (1000 * 60 * 60)) : 0;
    return Math.min(hours, 27);
  };

  const handleDeleteClick = (invitation: any) => {
    setSelectedInvitation(invitation);
    setShowDeleteModal(true);
  };

  const handleActivateClick = (invitation: any) => {
    setSelectedInvitation(invitation);
    setShowActivateModal(true);
  };

  const handleCopyLink = (invitation: any) => {
    const link = `${baseUrl}/UserInvitationPage/${invitation.invitation_code}`;
    navigator.clipboard.writeText(link)
      .then(() => toast.success('Enlace copiado con éxito'))
      .catch(() => toast.error('Error al copiar el enlace'));
  };

  const handleConfirmDelete = async () => {
    if (selectedInvitation) {
      try {
        await deleteUserInvitation(selectedInvitation.invitation_id);
        toast.success('Invitación eliminada con éxito');
        const remaining = OriginalInvitations.filter(inv => inv.invitation_id !== selectedInvitation.invitation_id);
        setData(remaining);
        setOriginalInvitations(remaining);
      } catch {
        toast.error('Error al eliminar invitación');
      }
    }
    setShowDeleteModal(false);
  };

  const handleConfirmActivate = async () => {
    if (selectedInvitation) {
      try {
        const resp = await reactivateUserInvitation(selectedInvitation.invitation_id);

        // Forzar nuevo timestamp local para simular reactivación reciente
        const now = new Date().toISOString();
        const updatedInvitation = {
          ...resp.data,
          timestamp: now,
          expiration_timestamp: null // Forzar recálculo con timestamp
        };

        toast.success('Invitación reactivada con éxito');
        const others = OriginalInvitations.filter(inv => inv.invitation_id !== selectedInvitation.invitation_id);
        const updated = [updatedInvitation, ...others];
        setData(updated);
        setOriginalInvitations(updated);
      } catch {
        toast.error('Error al reactivar invitación');
      }
    }
    setShowActivateModal(false);
  };

  const handleCreateInvitation = (newInvitation: any) => {
    setData([newInvitation, ...data]);
  };

  return (
    <>
      <div className="mp-table-card">
        <table className="mp-table">
          <thead className="mp-table-head">
            <tr>
              <th className="no-responsive">Código</th>
              <th>Invitado</th>
              <th>Puesto</th>
              <th className="no-responsive">Estado</th>
              <th>Tiempo validez</th>
              <th>{isMobile ? <FaCog /> : 'Acciones'}</th>
            </tr>
          </thead>
          <tbody className="mp-table-body">
            {data.map((item: any, index: number) => (
              <tr key={index}>
                <td className="no-responsive">{item.invitation_code}</td>
                <td>{item.nombre}</td>
                <td>{item.puesto_name}</td>
                <td className="no-responsive">{item.valid ? 'Válido' : 'Inválido'}</td>
                <td>{item.valid ? `${calculateRemainingHours(item)} horas` : 'Expirada'}</td>
                <td>
                  <div className="flex items-center gap-1 justify-center">
                    <button className="mp-btn-icon" onClick={() => handleActivateClick(item)}>
                      <HiOutlineArrowPath size={16} />
                    </button>
                    <button className="mp-btn-icon mp-btn-icon-danger" onClick={() => handleDeleteClick(item)}>
                      <HiOutlineTrash size={16} />
                    </button>
                    <button className="mp-btn-icon" onClick={() => handleCopyLink(item)}>
                      <HiOutlineClipboardDocument size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UserInvitation
        show={showInvitationModal}
        handleClose={() => setShowInvitationModal(false)}
        onCreate={handleCreateInvitation}
      />

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <div className="form-modal-title-container">
            <Modal.Title className="form-modal-title">Eliminar invitación</Modal.Title>
          </div>
          <button type="button" className="modal-close-button" onClick={() => setShowDeleteModal(false)}><X size={22} /></button>
        </Modal.Header>
        <Modal.Body>¿Está seguro de querer eliminar esta invitación?</Modal.Body>
        <Modal.Footer>
          <div className="mp-modal-footer">
            <button className="mp-modal-btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
            <button className="mp-modal-btn-submit" onClick={handleConfirmDelete}>Eliminar</button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showActivateModal} onHide={() => setShowActivateModal(false)}>
        <Modal.Header>
          <div className="form-modal-title-container">
            <Modal.Title className="form-modal-title">Activar invitación</Modal.Title>
          </div>
          <button type="button" className="modal-close-button" onClick={() => setShowActivateModal(false)}><X size={22} /></button>
        </Modal.Header>
        <Modal.Body>¿Estás seguro de reactivar esta invitación?</Modal.Body>
        <Modal.Footer>
          <div className="mp-modal-footer">
            <button className="mp-modal-btn-cancel" onClick={() => setShowActivateModal(false)}>Cancelar</button>
            <button className="mp-modal-btn-submit" onClick={handleConfirmActivate}>Activar</button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TableUserInvitations;
